import React, { useEffect, useState } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import {
  GetAssetDetailsQuery,
} from "@src/generated/graphql";
import { client } from "../../../utils/api";
import { Typography } from "src/components/shared";
import { NewsComponent } from "src/components/shared/news";
import AiContent from "src/components/shared/content/AiContent";

type BlogDetailContentProps = {
  content: any[];
  mainImage: string;
  authors?: {
    image: { url: string };
    description: string;
    name: string;
    title: string;
  };
  linkListCollection: {
    items: any[];
  };
  listComponents: any[];
  aiContent: any;
};

const prepareRichTextJson = (contentNode: any) => {
  return {
    nodeType: "document",
    data: {},
    content: [contentNode],
  };
};

const extractEmbeddedAssets = async (richTextJson: any) => {
  const assets: GetAssetDetailsQuery[] = [];

  for (const node of richTextJson) {
    if (
      node.nodeType === "embedded-asset-block" &&
      node.data &&
      node.data.target
    ) {
      const assetId = node.data.target.sys.id;

      try {
        const assetDetails = await client.GetAssetDetails({ id: assetId });
        assets.push(assetDetails);
      } catch (error) {
        console.error("Error fetching asset:", error);
      }
    }
  }

  return assets;
};

const extractEmbeddedEntries = async (richTextJson: any) => {
  const entries: any[] = [];

  for (const node of richTextJson) {
    if (
      node.nodeType === "embedded-entry-block" &&
      node.data &&
      node.data.target
    ) {
      const entryId = node.data.target.sys.id;
      console.log('👤 Processing entry ID:', entryId);

      try {

        const startTime = performance.now();
        const authorDetails = await client.GetAuthorDetails({ id: entryId });
        const endTime = performance.now();

        entries.push(authorDetails);
      } catch (error: any) {
       
      }
    }
  }

  console.log('👤 Final entries count:', entries.length);
  return entries;
};

const isEmptyParagraph = (node) => {
  return (
    node.nodeType === BLOCKS.PARAGRAPH &&
    node.content.length === 1 &&
    node.content[0].nodeType === 'text' &&
    node.content[0].value === ''
  );
};

// Custom component to render embedded author entries as testimonial blocks
const EmbeddedAuthorRenderer = ({ authorEntry }: { authorEntry: any }) => {
  const authorData = authorEntry?.author || authorEntry;

  return (
    <div className="embedded-author-testimonial">
      {authorData?.description && (
        <div className="embedded-author-testimonial__quote font-medium">
          {authorData.description}
        </div>
      )}

      <div className="embedded-author-testimonial__author">
        {authorData?.image?.url &&
          <div className="embedded-author-testimonial__author-image">
            <img
              src={authorData?.image?.url}
              alt={authorData?.name}
              className="embedded-author-testimonial__img"
            />
          </div>
        }
        <div className="embedded-author-testimonial__author-info">
          <h4 className="embedded-author-testimonial__name">{authorData?.name}</h4>
          <p className="embedded_title regular">{authorData?.title}</p>
        </div>
      </div>
    </div>
  );
};

const RichTextRenderer = ({
  richTextJson,
  embeddedAssets,
  embeddedEntries,
}: {
  richTextJson: any;
  embeddedAssets: any[];
  embeddedEntries: any[];
}) => {
  const options = {
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) => (
        <span className="bold">{text}</span>
      ),

      [MARKS.ITALIC]: (text: React.ReactNode) => (
        <em>{text}</em>
      ),

      [MARKS.UNDERLINE]: (text: React.ReactNode) => (
        <u>{text}</u>
      ),

      [MARKS.CODE]: (text: React.ReactNode) => (
        <code className="inline-code" style={{
          backgroundColor: '#f4f4f4',
          padding: '2px 4px',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '0.9em'
        }}>{text}</code>
      ),
    },

    renderNode: {
      [BLOCKS.HEADING_1]: (node: any, children: React.ReactNode) => (
        <h1 className="blog-heading-1">{children}</h1>
      ),

      [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => (
        <h2 className="blog-heading-2">{children}</h2>
      ),

      [BLOCKS.HEADING_3]: (node: any, children: React.ReactNode) => (
        <h3 className="blog-heading-3">{children}</h3>
      ),

      [BLOCKS.HEADING_4]: (node: any, children: React.ReactNode) => (
        <h4 className="blog-heading-4">{children}</h4>
      ),

      [BLOCKS.HEADING_5]: (node: any, children: React.ReactNode) => (
        <h5 className="blog-heading-5">{children}</h5>
      ),

      [BLOCKS.HEADING_6]: (node: any, children: React.ReactNode) => (
        <h6 className="blog-heading-6">{children}</h6>
      ),
      

      [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
        if (isEmptyParagraph(node)) {
          return <br />;
        }
        return <p style={{ lineHeight: "34px", marginBottom: "1rem" }}>{children}</p>;
      },

      [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => (
        <ul className="blog-list-ul">{children}</ul>
      ),

      [BLOCKS.OL_LIST]: (node: any, children: React.ReactNode) => (
        <ol className="blog-list-ol">{children}</ol>
      ),

      [BLOCKS.LIST_ITEM]: (node: any, children: React.ReactNode) => (
        <li className="blog-list-item">{children}</li>
      ),

      [BLOCKS.QUOTE]: (node: any, children: React.ReactNode) => (
        <blockquote className="quote-detail" style={{
          borderLeft: '4px solid #8773da40',
          paddingLeft: '1rem',
          margin: '1.5rem 0',
          color: "#131327",
          padding: '24px 36px',
          
        }}>{children}</blockquote>
      ),

      [BLOCKS.HR]: () => (
        <hr style={{
          border: 'none',
          borderTop: '1px solid #e5e7eb',
          margin: '2rem 0'
        }} />
      ),


      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        const assetId = node?.data?.target?.sys?.id;

        if (!assetId) {
          return <div className="error-message">Embedded asset not found.</div>;
        }

        const asset = embeddedAssets.find((_, index) => {
          return embeddedAssets[index]?.asset?.url.includes(assetId);
        });

        if (!asset) {
          return <div className="error-message">Loading asset...</div>;
        }

        const { url, description, fileName } = asset.asset;

        return (
          <div className="embedded-asset" style={{ marginBottom: '1.5rem' }}>
            <img
              src={url}
              alt={description || fileName}
              className="embedded-image"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: '0.375rem'
              }}
            />
            {description && (
              <p className="asset-description" style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                textAlign: 'center',
                marginTop: '0.5rem',
                fontStyle: 'italic'
              }}>{description}</p>
            )}
          </div>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        const entryId = node?.data?.target?.sys?.id;

        if (!entryId) {
          return <div className="error-message">Embedded author not found.</div>;
        }

        const authorEntry = embeddedEntries.find((entryData) => {
          return entryData?.author?.sys?.id === entryId;
        });

        if (!authorEntry) {
          return <div className="error-message">Loading author...</div>;
        }

        return (
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: '1.5rem'
          }}>
            <EmbeddedAuthorRenderer authorEntry={authorEntry} />
          </div>
        );
      },
    },
    renderText: (text: string) => {
      return text.split('\n').reduce((children: React.ReactNode[], textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
  };

  return (
    <div className="rich-text-content" style={{ maxWidth: '100%' }}>
      {documentToReactComponents(richTextJson, options)}
    </div>
  );
};

export const BlogDetailContent: React.FC<BlogDetailContentProps> = ({
  content,
  authors,
  linkListCollection,
  listComponents,
  aiContent,
}) => {
  console.log("Content", content);
  const [embeddedAssets, setEmbeddedAssets] = useState<any[]>([]);
  const [embeddedEntries, setEmbeddedEntries] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const assets = await extractEmbeddedAssets(content);
      const authorEntries = await extractEmbeddedEntries(content);

      setEmbeddedAssets(assets);
      setEmbeddedEntries(authorEntries);
    };
    fetchData();
  }, []);

  return (
    <section className="blog-component-detail blog-body">
      <div className="container-fluid custom-spacer-col">
        <div className="row">
          {/* <div className="d-none col-md-1 d-md-block"></div> */}
          <div className="col-md-10 col-sm-12 text-left blog_content mx-auto">
            <Typography tag="h4" className="blog-detail-title bold" >
              {aiContent?.title}
            </Typography>
            {(aiContent?.eyeBrowText && aiContent?.description) && <AiContent
              eyeBrowText={aiContent?.eyeBrowText}
              description={aiContent?.description}
            />}
            {content.map((contentNode, index) => {
              const richTextJson = prepareRichTextJson(contentNode);
              return (
                <RichTextRenderer
                  key={index}
                  richTextJson={richTextJson}
                  embeddedAssets={embeddedAssets}
                  embeddedEntries={embeddedEntries}
                />
              );
            })}

            <div className="social-links">
              {linkListCollection?.items.map((item) => (
                <a
                  key={item.sys?.id || item.linkUrl}
                  href={item.linkUrl}
                  target={item.openInNewWindow ? "_blank" : undefined}
                  rel={item.openInNewWindow ? "noopener noreferrer" : undefined}
                  className="social-links__link"
                  aria-label={item.linkText}
                >
                  <span className="social-links__icon">
                    <img
                      src={item.linkIcon.url}
                      alt={item.linkIcon.title || item.linkText}
                    />
                  </span>
                </a>
              ))}
            </div>

            <div className="author-container">
              {authors && (
                <div className="author-bio">
                  <div className="author-bio__header">
                    <div className="author-bio__image-wrapper" >
                      <img
                        src={authors.image?.url}
                        alt={authors.name}
                        className="author-bio__image"
                        style={{ background: authors.name === "Gradial" ? '#151428' : "" }}
                      />
                    </div>
                    <div className="author-bio__title-group">
                      <h3 className="author-bio__name">{authors.name}</h3>
                      <p className="author-bio__title">{authors.title}</p>
                    </div>
                  </div>
                  <p className="author-bio__description">{authors.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <NewsComponent content={listComponents[0]} />
    </section>
  );
};